import ImageGallery from "../components/ImageGallery";
import React, { useState, useEffect, useRef } from "react";
import api from "../api";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCog } from "@fortawesome/free-solid-svg-icons";
import { FiArrowLeft, FiUploadCloud } from "react-icons/fi";

const TheGallery = ({ event_id, event, admin = false, selectedAlbum, onAlbumChange }) => {
  const [images, setImages] = useState([]);
  const [statsProcessing, setStatsProcessing] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [albumImageNames, setAlbumImageNames] = useState(null);
  const [featuredAlbum, setFeaturedAlbum] = useState(null);
  const navigate = useNavigate();
  const hasFetchedRef = useRef(null);

  const fetchImages = async () => {
    setIsUploading(true);
    try {
      const response = await api.get(`/images?eid=${event_id}`);
      if (typeof response.data !== "object") return;
      setImages(response.data);
    } catch (error) {
      console.error("Error fetching images:", error);
    } finally {
      setIsUploading(false);
    }
  };

  // Album the photographer chose to show first (guest gallery only)
  const fetchFeaturedAlbum = async () => {
    try {
      const response = await api.get(`/albums/featured?event_id=${event_id}`);
      setFeaturedAlbum(response.data?.album || null);
    } catch {
      setFeaturedAlbum(null); // non-critical — fall back to the flat gallery
    }
  };

  const fetchStatsProcessing = async () => {
    const poll = async () => {
      try {
        const response = await api.get(`/processed_images_stats?eid=${event_id}`);
        if (typeof response.data !== "object") return;
        setStatsProcessing(response.data);
        if (response.data.is_processing) setTimeout(poll, 2000);
      } catch (error) {
        console.error("Error fetching image stats:", error);
      }
    };
    poll();
  };

  useEffect(() => {
    if (!event_id) return;
    if (hasFetchedRef.current === event_id) return;
    hasFetchedRef.current = event_id;
    setImages([]);
    setStatsProcessing({});
    setFeaturedAlbum(null);
    fetchImages();
    if (!admin) fetchFeaturedAlbum();
    fetchStatsProcessing();
  }, [event_id]);

  useEffect(() => {
    if (!selectedAlbum) {
      setAlbumImageNames(null);
      return;
    }
    api
      .get(`/albums/${selectedAlbum.album_id}`, { withCredentials: true })
      .then((res) => setAlbumImageNames(new Set(res.data.images || [])))
      .catch(() => setAlbumImageNames(null));
  }, [selectedAlbum]);

  const displayedImages = albumImageNames
    ? images.filter((img) => albumImageNames.has(img.name))
    : images;

  if (!event) return null;

  return (
    <div style={{ flex: 1, minWidth: 0, padding: "0 4px 4px" }}>

      {/* ── Loading ── */}
      {isUploading && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 0", gap: 10, color: "#9ca3af" }}>
          <style>{`@keyframes tg-spin { to { transform: rotate(360deg); } }`}</style>
          <div style={{
            width: 18, height: 18,
            border: "2px solid #e5e7eb",
            borderTopColor: "#6366f1",
            borderRadius: "50%",
            animation: "tg-spin 0.75s linear infinite",
          }} />
          <span style={{ fontSize: 13, fontWeight: 500 }}>Loading photos…</span>
        </div>
      )}

      {/* ── Gallery ── */}
      {!isUploading && displayedImages.length > 0 && (
        <>
          {/* Sub-header: album breadcrumb + admin stats */}
          {(selectedAlbum || (admin && statsProcessing.nb_processed_images != null)) && (
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "8px 14px 2px",
              minHeight: 36,
            }}>
              {/* Left: back button + album title */}
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {selectedAlbum && onAlbumChange && (
                  <button
                    onClick={() => onAlbumChange(null)}
                    title="Back to all photos"
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 4,
                      height: 26, padding: "0 9px 0 6px",
                      borderRadius: 7,
                      border: "1px solid #ebebeb",
                      background: "#fff",
                      color: "#6b7280",
                      fontSize: 12, fontWeight: 500,
                      cursor: "pointer",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                      transition: "all 0.15s",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.color = "#374151"; e.currentTarget.style.background = "#f9fafb"; }}
                    onMouseLeave={e => { e.currentTarget.style.color = "#6b7280"; e.currentTarget.style.background = "#fff"; }}
                  >
                    <FiArrowLeft size={12} />
                    All photos
                  </button>
                )}
                {selectedAlbum && (
                  <span style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: "1.15rem", fontWeight: 600,
                    color: "#374151", letterSpacing: "0.3px",
                  }}>
                    {selectedAlbum.name}
                  </span>
                )}
              </div>

              {/* Right: processing stats (admin only) */}
              {admin && statsProcessing.nb_processed_images != null && (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 11, color: "#9ca3af" }}>
                    Processed: {statsProcessing.nb_processed_images}
                  </span>
                  {statsProcessing.is_processing && (
                    <>
                      <span style={{
                        fontSize: 10, fontWeight: 600, color: "#6366f1",
                        background: "#eef2ff", padding: "1px 7px", borderRadius: 10,
                      }}>
                        {statsProcessing.processed_images_percent}%
                      </span>
                      <FontAwesomeIcon icon={faCog} spin style={{ fontSize: 12, color: "#6366f1" }} />
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          <ImageGallery
            event={event}
            images={displayedImages}
            show_delete={admin}
            setImages={setImages}
            watermarkEnabled={!admin && (event?.watermark_enabled ?? true)}
            showInteractions={true}
            allImages={images}
            onAlbumCreated={fetchImages}
            selectedAlbum={selectedAlbum}
            onAlbumChange={onAlbumChange}
            featuredSection={!admin && !selectedAlbum ? featuredAlbum : null}
            onAlbumImagesRemoved={(removedNames) => {
              setAlbumImageNames((prev) => {
                if (!prev) return prev;
                const next = new Set(prev);
                removedNames.forEach((n) => next.delete(n));
                return next;
              });
            }}
          />
        </>
      )}

      {/* ── Empty states ── */}
      {!isUploading && displayedImages.length === 0 && (
        admin ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "80px 24px", gap: 16 }}>
            <div style={{
              width: 56, height: 56, borderRadius: 16,
              background: "#f5f5f7",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <FiUploadCloud size={24} color="#9ca3af" />
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#1a1a1a", marginBottom: 4 }}>No photos yet</div>
              <div style={{ fontSize: 13, color: "#9ca3af" }}>Upload your first images to get started</div>
            </div>
            <button
              onClick={() => navigate(`/admin/upload/${event.id_name}/`)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                padding: "10px 22px", borderRadius: 12,
                border: "none", background: "#1a1a1a", color: "#fff",
                fontSize: 13, fontWeight: 600, cursor: "pointer",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                transition: "all 0.15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "#333"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,0.2)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#1a1a1a"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)"; }}
            >
              <FiUploadCloud size={15} />
              Upload photos
            </button>
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "80px 24px" }}>
            <div style={{ fontSize: 14, fontWeight: 500, color: "#374151", marginBottom: 4 }}>
              {selectedAlbum ? `No photos in "${selectedAlbum.name}"` : "No photos yet"}
            </div>
            <div style={{ fontSize: 12, color: "#9ca3af" }}>Check back soon.</div>
          </div>
        )
      )}
    </div>
  );
};

export default TheGallery;
